import { useState, useEffect, useRef, useMemo } from "react";
import { Navigate, useSearchParams, useNavigate, useParams, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  AreaChart,
  Area,
} from "recharts";
import {
  ArrowLeft,
  ExternalLink,
  DollarSign,
  Package,
  Calendar,
  MapPin,
  Copy,
  Heart,
  Bell,
  TrendingUp,
  Eye,
  Share2,
  MonitorOff,
  Loader2,
} from "lucide-react";
import { getProduct, getPublicAffiliateLink } from "../services/ProductsService";
import { createDeal, getDealShareUrl } from "../services/DealsService";
import type { Product } from "../types/Product";
import { toast } from "react-toastify";
import { isExtraOnlineRetailer } from "../config/retailers";
import {
  addTrackerWithRetailer,
  removeTrackerWithRetailer,
  isProductTrackedInStore,
  type AddTrackerRequest,
} from "../services/TrackingService";
import {
  addHomeDepotTracker,
  removeHomeDepotTracker,
  isHomeDepotProductTracked,
  type AddHomeDepotTrackerRequest,
} from "../services/HomeDepotTrackingService";
import {
  // addFavorite,
  // removeFavorite,
  removeFavoriteWithRetailer,
  quickSaveFavoriteWithRetailer,
  // checkFavoriteWithRetailer,
} from "../services/FavoritesService";
import { useQueryClient } from "@tanstack/react-query";
import { favoritesKeys } from "../hooks/useFavorites";
import StoreLocationCard from "../components/StoreLocationCard";
import { useUserContext } from "../context/UserContext";
import BarcodeComponent from "../components/Barcode";
import ProductDetailSkeleton from "../components/ProductDetailSkeleton";
import OtherStoreAvailability from "../components/OtherStoreAvailability";
import { getProductImageUrl } from "../utils/imageUtils";
import { getRetailerLinkLabel } from "../utils/productUrls";
import { useFavoritesContext } from "../context/FavoritesContext";
import { useTheme } from "../context/ThemeContext";
import { useTranslation } from "../context/LanguageContext";
import { isFreeTier } from "../utils/userUtils";
import { isPublicOnlineProductContext } from "../components/ProductDetailModal.utils";
import { formatStoreId, getMultiBuyPriceDisplay } from "../components/ProductItem.utils";
import { ProductOfferPrice } from "../components/ProductOfferPrice";

// Currency formatting utility
const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(amount);
};

interface ChartDataPoint {
  timestamp: number;
  date: string;
  price: number;
  inventory: number;
}

export default function ProductDetailPage() {
	const [searchParams] = useSearchParams();
	const { retailer } = useParams<{ retailer: string }>();
	const location = useLocation();
	const { user } = useUserContext();
	const storeId = searchParams.get("storeId");

	if (isFreeTier(user) && !isPublicOnlineProductContext(retailer, storeId)) {
		const source = encodeURIComponent("product_detail_lock");
		return (
			<Navigate
				to={`/settings?tab=billing&upgradeSource=${source}`}
				replace
				state={{ returnTo: `${location.pathname}${location.search}` }}
			/>
		);
	}

	return <ProductDetailPageContent />;
}

function ProductDetailPageContent() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { retailer, productId } = useParams<{ retailer: string; productId: string }>();
  const isAmazon = retailer === "amazon";
  const isLowes = retailer === "lowes";
  const isKohls = retailer === "kohls";
  const isWoot = retailer === "woot";
  const isOnlineOnly = isAmazon || isLowes || isKohls || isWoot || isExtraOnlineRetailer(retailer);
  const productIdentifier = isOnlineOnly
    ? productId || searchParams.get("upc") || searchParams.get("sku")
    : productId || searchParams.get("sku");
  const storeId = isOnlineOnly ? searchParams.get("storeId") || "online" : searchParams.get("storeId");
  const productIndex = searchParams.get("productIndex");
  
  // Check if this is being rendered in a modal context
  const isModal = (location.state as any)?.modal === true;
  const containerRef = useRef<HTMLDivElement>(null);
  
  // Synchronously check if we're in a portal by looking for the modal backdrop
  // This check happens during render, so we need to be careful about timing
  const isInPortal = useMemo(() => {
    if (!isModal) return false;
    // Check if there's a modal backdrop in the DOM
    // This will be true if we're rendering inside the portal
    const modalBackdrop = document.querySelector('[data-product-modal="true"]');
    return !!modalBackdrop;
  }, [isModal]);
  
  // If this is a modal route, we need to check if we're rendering in the route vs the portal
  // The route should not render when modal is open - only the portal version should render
  if (isModal) {
    // If we're not in a portal, this means we're rendering in the route
    // Return null to prevent the route from showing
    if (!isInPortal) {
      return null;
    }
  }

  // Check if this is a Home Depot product
  const isHomeDepot = retailer === "home_depot";
  const isDollarGeneral = retailer === "dollar_general";

  // State management for favorites and tracking
  const { isFavorite: checkIsFavorite } = useFavoritesContext();
  const favoriteStatus = {
    isFavorite: productIdentifier ? checkIsFavorite(productIdentifier) : false,
    isLoading: false,
  };
  const [affiliateLoading, setAffiliateLoading] = useState(false);
  const [shareLoading, setShareLoading] = useState(false);
  const [isUpdatingFavorite, setIsUpdatingFavorite] = useState(false);
  const [isTracked, setIsTracked] = useState(false);
  const [isTrackingProduct, setIsTrackingProduct] = useState(false);
  const [showTrackingModal, setShowTrackingModal] = useState(false);
  const [targetPrice, setTargetPrice] = useState("");

  const { user } = useUserContext();
  const isFreeUser = isFreeTier(user);
  const { isDark } = useTheme();
  const { t } = useTranslation();

  const {
    data: product,
    isLoading,
    error,
  } = useQuery<Product>({
    queryKey: ["product", productIdentifier, storeId, retailer],
    queryFn: () => getProduct(productIdentifier!, storeId!, retailer),
    enabled: !!(productIdentifier && storeId),
    retry: false,
  });

  // Check tracking and favorite status on load
  useEffect(() => {
    const checkTrackingStatus = async () => {
      if (product?.upc) {
        try {
          if (isHomeDepot) {
            // For Home Depot, check using the Home Depot service
            const itemId = (product as any).itemId || product.sku;
            const tracked = await isHomeDepotProductTracked(itemId);
            setIsTracked(tracked);
          } else {
            // For other retailers, check tracking status for this specific store
            const tracked = await isProductTrackedInStore(
              product.upc,
              product.storeId,
              retailer || "walmart"
            );
            setIsTracked(tracked);
          }
        } catch (error) {
          console.error("Error checking tracking status:", error);
        }
      }
    };

    checkTrackingStatus();
  }, [product?.upc, product?.storeId, retailer, isHomeDepot]);

  // Process data for charts
  const chartData: ChartDataPoint[] = [];
  if (product) {
    const prices = product.storePrice || [];
    const inventory = product.salesFloor || [];
    const timestamps = product.lastUpdates || [];

    const maxLength = Math.min(
      prices.length,
      inventory.length,
      timestamps.length
    );
    for (let i = 0; i < maxLength; i++) {
      chartData.push({
        timestamp: timestamps[i],
        date: new Date(timestamps[i]).toLocaleDateString(),
        price: prices[i],
        inventory: inventory[i],
      });
    }
  }

  const handleBack = () => {
    // If we're in modal mode, navigate back to restore the previous URL
    // This will trigger the modal close handler in ProductItem
    if (isModal) {
      navigate(-1);
      return;
    }
    
    // If we have a product index, navigate back to the products page with returnToIndex parameter
    if (productIndex && retailer) {
      const productsUrl = `/${retailer}/products?returnToIndex=${productIndex}`;
      navigate(productsUrl);
    } else {
      // Use react-router's back functionality to go to the previous page
      navigate(-1);
    }
  };

  // Handle favorite toggle
  const handleFavoriteToggle = async () => {
    if (isUpdatingFavorite || !product?.upc) return;

    setIsUpdatingFavorite(true);

    try {
      if (favoriteStatus.isFavorite) {
        await removeFavoriteWithRetailer(product.upc, retailer || "walmart");
        // Invalidate both the lightweight UPC cache and full favorites list
        queryClient.invalidateQueries({
          queryKey: ["favorite-upcs", retailer || "walmart"],
        });
        queryClient.invalidateQueries({
          queryKey: favoritesKeys.listWithRetailer(1000, retailer || "walmart"),
        });
        toast.success("Removed from favorites");
      } else {
        // Use the new quick save API for faster favorites adding
        const result = await quickSaveFavoriteWithRetailer(
          product.upc,
          retailer || "walmart",
          product.name,
          product.image,
          storeId === "online" ? "online" : product.storeId || storeId || undefined
        );

        if (!result.alreadyExists) {
          // Invalidate both the lightweight UPC cache and full favorites list
          queryClient.invalidateQueries({
            queryKey: ["favorite-upcs", retailer || "walmart"],
          });
          queryClient.invalidateQueries({
            queryKey: favoritesKeys.listWithRetailer(
              1000,
              retailer || "walmart"
            ),
          });
          toast.success("Added to favorites");
        } else {
          toast.info("Product already in favorites");
        }
      }
    } catch (error: unknown) {
      const message =
        error instanceof Error ? error.message : "Failed to update favorites";
      toast.error(message);
    } finally {
      setIsUpdatingFavorite(false);
    }
  };

  // Handle track button click
  const handleTrackClick = async () => {
    if (!product) return;

    if (isTracked) {
      // Remove tracking
      try {
        setIsTrackingProduct(true);
        if (isHomeDepot) {
          const itemId = (product as any).itemId || product.sku;
          await removeHomeDepotTracker(itemId);
        } else {
          await removeTrackerWithRetailer(product.upc, retailer || "walmart");
        }
        setIsTracked(false);
        toast.success("Stopped tracking product");
      } catch {
        toast.error("Failed to stop tracking");
      } finally {
        setIsTrackingProduct(false);
      }
    } else {
      // Show tracking modal
      const suggestedPrice = Math.max(0.01, currentPrice * 0.9);
      setTargetPrice(suggestedPrice.toFixed(2));
      setShowTrackingModal(true);
    }
  };

  // Handle tracking confirmation
  const handleConfirmTracking = async () => {
    if (!product || !targetPrice) return;

    const targetPriceNum = parseFloat(targetPrice);

    if (isNaN(targetPriceNum) || targetPriceNum <= 0) {
      toast.error("Please enter a valid target price");
      return;
    }

    if (targetPriceNum >= currentPrice) {
      toast.error(
        `Target price must be lower than current price ($${currentPrice.toFixed(
          2
        )})`
      );
      return;
    }

    setIsTrackingProduct(true);

    try {
      if (isHomeDepot) {
        // For Home Depot, use the Home Depot service with zip code
        const homeDepotRequest: AddHomeDepotTrackerRequest = {
          itemId: (product as any).itemId || product.sku, // Use itemId field for Home Depot, fallback to sku
          upc: product.upc, // Pass the actual UPC if available
          name: product.name,
          image: product.image,
          category: product.category,
          zipCode: user?.zipCode || "", // Use user's zip code
          targetPrice: targetPriceNum,
          currentPrice: currentPrice,
          msrp: product.msrp,
          url: product.url,
        };

        if (!homeDepotRequest.zipCode) {
          toast.error(
            "Please set your zip code in your profile to track HD products"
          );
          return;
        }

        await addHomeDepotTracker(homeDepotRequest);
      } else {
        // For other retailers, use the regular service
        const trackerRequest: AddTrackerRequest = {
          upc: product.upc,
          zipCode: user?.zipCode || "", // Use user's zip code
          targetPrice: targetPriceNum,
        };

        if (!trackerRequest.zipCode) {
          toast.error(
            "Please set your zip code in your profile to track products"
          );
          return;
        }

        await addTrackerWithRetailer(trackerRequest, retailer || "walmart", user?.accountType, user?.maxAlertsOverride);
      }

      setIsTracked(true);
      setShowTrackingModal(false);
      setTargetPrice("");
      toast.success("Started tracking product");
    } catch (error) {
      console.error("Error adding tracker:", error);
      toast.error("Failed to start tracking");
    } finally {
      setIsTrackingProduct(false);
    }
  };

  if (!productIdentifier || !storeId) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50 dark:bg-dark-950">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
            {t("errors.pageNotFound")}
          </h1>
          <p className="text-gray-600 dark:text-dark-300 mb-6">
            Product ID and Store ID are required parameters.
          </p>
          <button
            onPointerDown={() =>
              navigate(
                `/${retailer}/products${storeId ? `?storeId=${storeId}` : ""}`
              )
            }
            className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-2 rounded-lg transition-colors"
          >
            {t("nav.products")}
          </button>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return <ProductDetailSkeleton />;
  }

  if (error || !product) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50 dark:bg-dark-950">
        <div className="text-center">
          <div className="mb-6">
            <Package className="mx-auto h-16 w-16 text-gray-400 dark:text-dark-500" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
            {t("productSearch.productNotFound")}
          </h1>
          <p className="text-gray-600 dark:text-dark-300 mb-6">
            The product with ID "{productIdentifier}" could not be found in store "{storeId}
            ".
          </p>
          <div className="space-x-4">
            <button
              onPointerDown={handleBack}
              className="bg-gray-500 hover:bg-gray-600 text-white px-6 py-2 rounded-lg transition-colors"
            >
              {t("errors.goBack")}
            </button>
            <button
              onPointerDown={() =>
                navigate(
                  `/${retailer}/products${storeId ? `?storeId=${storeId}` : ""}`
                )
              }
              className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-2 rounded-lg transition-colors"
            >
              {t("nav.products")}
            </button>
          </div>
        </div>
      </div>
    );
  }

  const currentPrice = product.storePrice?.[product.storePrice.length - 1] || 0;
  const multiBuy = getMultiBuyPriceDisplay(product, currentPrice, product.msrp || 0);
  const currentInventory =
    product.salesFloor?.[product.salesFloor.length - 1] || 0;
  const lastUpdated = product.lastUpdated
    ? new Date(product.lastUpdated)
    : null;

  // Helper functions
  const copyToClipboard = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied to clipboard!`);
    } catch (err) {
      toast.error("Failed to copy to clipboard");
    }
  };

  const handleAffiliateClick = async (e: React.MouseEvent) => {
    e.preventDefault();
    if (affiliateLoading || !product?.productKey) return;
    const tab = window.open("about:blank", "_blank");
    setAffiliateLoading(true);
    try {
      const trackingUrl = await getPublicAffiliateLink(product.productKey);
      if (tab) tab.location.href = trackingUrl;
      else window.location.href = trackingUrl;
    } catch {
      if (tab) tab.close();
    } finally {
      setAffiliateLoading(false);
    }
  };

  const discountPercent =
    product.msrp > currentPrice
      ? Math.round(((product.msrp - currentPrice) / product.msrp) * 100)
      : 0;

  const savingsAmount =
    product.onlinePrice > currentPrice ? product.onlinePrice - currentPrice : 0;

  return (
    <div ref={containerRef} className="min-h-screen bg-gray-50 dark:bg-dark-950">
      {/* Mobile-First Header */}
      <div className="bg-white dark:bg-dark-900 sticky top-0 z-40 border-b border-gray-200 dark:border-dark-700">
        <div className="px-4 py-3">
          <div className="flex items-center justify-between">
            <button
              onPointerDown={handleBack}
              className="flex items-center text-gray-700 dark:text-dark-200 hover:text-primary-600 dark:hover:text-primary-400 transition-colors text-sm font-medium"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              {t("common.back")}
            </button>

            {/* Quick Actions */}
            <div className="flex items-center space-x-2">
              <button
                onPointerDown={async () => {
                  if (shareLoading || !product) return;
                  setShareLoading(true);
                  try {
                    const currentPrice = product.storePrice?.[product.storePrice.length - 1] || 0;
                    const dealId = await createDeal({
                      upc: product.upc, sku: product.sku, name: product.name,
                      msrp: product.msrp, onlinePrice: product.onlinePrice,
                      storePrice: currentPrice, image: product.image,
                      storeId: product.storeId, retailer: retailer || "walmart",
                      category: product.category, url: product.url || "",
                      aisle: product.aisle, inStock: product.inStock,
                      discountPercentage: product.discountPercentage,
                    });
                    await navigator.clipboard.writeText(getDealShareUrl(dealId));
                    toast.success("Share link copied!");
                  } catch { toast.error("Failed to create share link"); }
                  finally { setShareLoading(false); }
                }}
                disabled={shareLoading}
                className="p-2 text-gray-500 dark:text-dark-400 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-gray-100 dark:hover:bg-dark-800 rounded-lg transition-colors disabled:opacity-50"
                title="Share product"
              >
                <Share2 className="h-4 w-4" />
              </button>
              {!isFreeUser && <button
                  onPointerDown={handleFavoriteToggle}
                  disabled={isUpdatingFavorite}
                  className={`p-2 rounded-lg transition-colors ${
                    favoriteStatus.isFavorite
                      ? "text-red-500 bg-red-50 dark:bg-red-900/30 hover:bg-red-100 dark:hover:bg-red-900/50"
                      : "text-gray-500 dark:text-dark-400 hover:text-red-500 hover:bg-gray-100 dark:hover:bg-dark-800"
                  } ${isUpdatingFavorite ? "opacity-50" : ""}`}
                  title="Add to favorites"
                >
                  <Heart
                    className={`h-4 w-4 ${
                      favoriteStatus.isFavorite ? "fill-current" : ""
                    }`}
                  />
                </button>}
              {!isWoot && (
                <button
                  onPointerDown={handleTrackClick}
                  disabled={isTrackingProduct}
                  className={`p-2 rounded-lg transition-colors ${
                    isTracked
                      ? "text-emerald-500 bg-emerald-50 dark:bg-emerald-900/30 hover:bg-emerald-100 dark:hover:bg-emerald-900/50"
                      : "text-gray-500 dark:text-dark-400 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-gray-100 dark:hover:bg-dark-800"
                  } ${isTrackingProduct ? "opacity-50" : ""}`}
                  title="Set price alert"
                >
                  {isTrackingProduct ? (
                    <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                  ) : isTracked ? (
                    <MonitorOff className="h-4 w-4" />
                  ) : (
                    <Bell className="h-4 w-4" />
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-3 py-3 pb-32 sm:pb-4">
        {/* Mobile-First Product Layout */}
        <div className="bg-white dark:bg-dark-900 border border-gray-200 dark:border-dark-700 rounded-lg mb-3">
          <div className="p-3">
            {/* Top Row: Image + Product Info */}
            <div className="flex gap-3 mb-3">
              {/* Product Image - Top Left Corner */}
              <div className="relative flex-shrink-0">
                {product.image ? (
                  <div className="relative w-30 h-30 sm:w-24 sm:h-24">
                    <img
                      src={getProductImageUrl(product.image)}
                      alt={product.name}
                      className="w-full h-full object-cover rounded-lg"
                    />
                    {discountPercent > 0 && (
                      <div className="absolute -top-1 -right-1 bg-red-500 text-white px-1.5 py-0.5 rounded-md text-xs font-bold">
                        {discountPercent}%
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="w-20 h-20 sm:w-24 sm:h-24 bg-gray-100 dark:bg-dark-800 flex items-center justify-center rounded-lg">
                    <Package className="h-8 w-8 text-gray-400 dark:text-dark-500" />
                  </div>
                )}
              </div>

              {/* Product Info - Top Right */}
              <div className="flex-1 space-y-1.5">
                <div className="flex items-center justify-between bg-gray-50 dark:bg-dark-800 border border-gray-200 dark:border-dark-700 px-2.5 py-1.5 rounded-md text-xs">
                  <div className="flex items-center">
                    <Package className="h-3 w-3 mr-1.5 text-gray-500 dark:text-dark-400" />
                    <span className="text-gray-600 dark:text-dark-300">
                      {isHomeDepot ? "Item ID" : "SKU"}:
                    </span>
                    <span className="ml-1 font-mono text-gray-900 dark:text-dark-100 text-xs">
                      {product.sku}
                    </span>
                  </div>
                  <button
                    onPointerDown={() =>
                      copyToClipboard(
                        product.sku,
                        isHomeDepot ? "Item ID" : "SKU"
                      )
                    }
                    className="p-0.5 hover:bg-gray-200 dark:hover:bg-dark-700 rounded transition-colors"
                  >
                    <Copy className="h-3 w-3 text-gray-500 dark:text-dark-400" />
                  </button>
                </div>

                <div className="flex items-center justify-between bg-gray-50 dark:bg-dark-800 border border-gray-200 dark:border-dark-700 px-2.5 py-1.5 rounded-md text-xs">
                  <div className="flex items-center">
                    <span className="text-gray-600 dark:text-dark-300">UPC:</span>
                    <span className="ml-1 font-mono text-gray-900 dark:text-dark-100 text-xs">
                      {product.upc}
                    </span>
                  </div>
                  <button
                    onPointerDown={() => copyToClipboard(product.upc, "UPC")}
                    className="p-0.5 hover:bg-gray-200 dark:hover:bg-dark-700 rounded transition-colors"
                  >
                    <Copy className="h-3 w-3 text-gray-500 dark:text-dark-400" />
                  </button>
                </div>

                <div className="flex items-center bg-purple-100 dark:bg-purple-900/30 border border-primary-200 dark:border-purple-700/50 px-2.5 py-1.5 rounded-md text-xs">
                  <MapPin className="h-3 w-3 mr-1.5 text-primary-600 dark:text-primary-400" />
                  <span className="text-gray-600 dark:text-dark-300">{isOnlineOnly ? "Channel:" : "Store:"}</span>
                  <span className="ml-1 font-semibold text-primary-700 dark:text-primary-300">
                    {isOnlineOnly ? "Online" : `#${formatStoreId(product.storeId)}`}
                  </span>
                </div>
              </div>
            </div>

            {/* Product Title */}
            <div className="mb-3">
              <h1 className="text-md sm:text-lg font-bold text-gray-900 dark:text-white leading-tight">
                {product.name}
              </h1>
            </div>

            {/* Pricing Section */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-3">
              {/* Store Price */}
              <div className="bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-700/50 rounded-md p-2.5">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-medium text-emerald-700 dark:text-emerald-300">
                    {multiBuy ? t("products.dealPrice") : isOnlineOnly ? t("productSearch.price") : t("productSearch.storePrice")}
                  </span>
                  <DollarSign className="h-3 w-3 text-emerald-600 dark:text-emerald-400" />
                </div>
                {multiBuy ? (
                  <div className="flex flex-wrap items-baseline gap-x-1.5 gap-y-1">
                    <ProductOfferPrice display={multiBuy} />
                  </div>
                ) : (
                  <div className="text-xl font-bold text-emerald-900 dark:text-emerald-200">
                    {formatCurrency(currentPrice)}
                  </div>
                )}
                {!multiBuy && savingsAmount > 0 && (
                  <div className="mt-1">
                    <div className="inline-flex items-center space-x-1 text-orange-700 dark:text-orange-300 bg-orange-100 dark:bg-orange-900/30 px-2 py-1 rounded-full">
                      <span className="text-xs font-bold">💰</span>
                      <span className="text-xs font-bold">
                        {t("products.saveAmount", { amount: formatCurrency(savingsAmount) })}
                      </span>
                    </div>
                  </div>
                )}
              </div>

              {/* Online Price - Hide for Home Depot and online-only retailers */}
              {!isHomeDepot && !isOnlineOnly && (
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700/50 rounded-md p-2.5">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-medium text-blue-700 dark:text-blue-300">
                      Online Price
                    </span>
                    <Eye className="h-3 w-3 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div className="text-xl font-bold text-blue-900 dark:text-blue-200">
                    {formatCurrency(product.onlinePrice)}
                  </div>
                  <div className="text-xs text-blue-700 dark:text-blue-400 mt-0.5">
                    {getRetailerLinkLabel(retailer)}
                  </div>
                </div>
              )}

              {/* MSRP */}
              <div className="bg-gray-50 dark:bg-dark-800 border border-gray-200 dark:border-dark-700 rounded-md p-2.5">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-medium text-gray-700 dark:text-dark-200">
                    MSRP
                  </span>
                  <TrendingUp className="h-3 w-3 text-gray-600 dark:text-dark-400" />
                </div>
                <div className="text-xl font-bold text-gray-900 dark:text-white">
                  {formatCurrency(product.msrp)}
                </div>
                <div className="text-xs text-gray-600 dark:text-dark-400 mt-0.5">Retail Price</div>
              </div>
            </div>

            {product.productKey && (
              <div className="mb-3">
                <button
                  onClick={handleAffiliateClick}
                  disabled={affiliateLoading}
                  className="inline-flex w-full sm:w-auto items-center justify-center px-3 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors text-sm font-medium border border-primary-600 disabled:opacity-60"
                >
                  {affiliateLoading ? <Loader2 className="h-3 w-3 mr-1.5 animate-spin" /> : <ExternalLink className="h-3 w-3 mr-1.5" />}
                  {getRetailerLinkLabel(retailer)}
                </button>
              </div>
            )}

            {/* Product Info Grid */}
            <div className="grid grid-cols-2 gap-2 mb-3">
              <div className="bg-gray-50 dark:bg-dark-800 border border-gray-200 dark:border-dark-700 rounded-md p-2.5">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-medium text-gray-700 dark:text-dark-200">
                    {t("products.availability")}
                  </span>
                  <div className={`w-1.5 h-1.5 rounded-full ${isOnlineOnly || currentInventory > 0 ? "bg-green-500" : "bg-red-500"}`} />
                </div>
                <div className="text-sm font-semibold text-gray-900 dark:text-white">
                  {isOnlineOnly ? t("products.inStock") : `${currentInventory} units`}
                </div>
              </div>

              {!isDollarGeneral && (
                <div className="bg-gray-50 dark:bg-dark-800 border border-gray-200 dark:border-dark-700 rounded-md p-2.5">
                  <span className="text-xs font-medium text-gray-700 dark:text-dark-200 block mb-1">
                    Location
                  </span>
                  <div className="text-sm font-semibold text-gray-900 dark:text-white">
                    {product.aisle ? `Aisle ${product.aisle}` : "N/A"}
                  </div>
                </div>
              )}

              <div className="bg-gray-50 dark:bg-dark-800 border border-gray-200 dark:border-dark-700 rounded-md p-2.5">
                <span className="text-xs font-medium text-gray-700 dark:text-dark-200 block mb-1">
                  {t("common.category")}
                </span>
                <div className="text-xs text-gray-900 dark:text-dark-100 line-clamp-1">
                  {product.category}
                </div>
              </div>

              <div className="bg-gray-50 dark:bg-dark-800 border border-gray-200 dark:border-dark-700 rounded-md p-2.5">
                <span className="text-xs font-medium text-gray-700 dark:text-dark-200 block mb-1">
                  Status
                </span>
                <div
                  className={`text-sm font-semibold ${
                    isOnlineOnly || product.inStock ? "text-green-600 dark:text-green-400" : "text-red-600 dark:text-red-400"
                  }`}
                >
                  {isOnlineOnly || product.inStock ? t("products.inStock") : t("products.outOfStock")}
                </div>
              </div>
            </div>

            {/* Barcode Section */}
            {(product.sku || product.upc) && (
              <div className="bg-gray-50 dark:bg-dark-800 rounded-lg p-3 mb-3">
                <div className="mb-2">
                  <span className="text-sm font-medium text-gray-700 dark:text-dark-200 block mb-1">
                    {t("products.productBarcode")}
                  </span>
                  <div className="flex flex-wrap gap-x-3 gap-y-2 text-xs text-gray-500 dark:text-dark-400 mb-3">
                    {product.upc && (
                      <div className="flex items-center gap-1">
                        <span>
                          {isHomeDepot ? "Item ID" : "SKU"}: {product.upc}
                        </span>
                        <button
                          onPointerDown={() => {
                            navigator.clipboard.writeText(product.upc);
                            toast.success(
                              `${
                                isHomeDepot ? "Item ID" : "SKU"
                              } copied to clipboard!`
                            );
                          }}
                          className="p-0.5 text-gray-400 dark:text-dark-500 hover:text-gray-600 dark:hover:text-dark-200 hover:bg-gray-100 dark:hover:bg-dark-700 rounded transition-colors"
                          title={`Copy ${isHomeDepot ? "Item ID" : "SKU"}`}
                        >
                          <Copy className="w-3 h-3" />
                        </button>
                      </div>
                    )}
                    {product.sku && (
                      <div className="flex items-center gap-1">
                        <span>UPC: {product.sku}</span>
                        <button
                          onPointerDown={() => {
                            navigator.clipboard.writeText(product.sku);
                            toast.success("UPC copied to clipboard!");
                          }}
                          className="p-0.5 text-gray-400 dark:text-dark-500 hover:text-gray-600 dark:hover:text-dark-200 hover:bg-gray-100 dark:hover:bg-dark-700 rounded transition-colors"
                          title="Copy UPC"
                        >
                          <Copy className="w-3 h-3" />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex justify-center">
                  <BarcodeComponent
                    upc={product.sku || product.upc}
                    width={1.5}
                    height={50}
                    fontSize={10}
                    displayValue={true}
                    clickable={false}
                    className="bg-white dark:bg-dark-900 border border-gray-200 dark:border-dark-700 rounded-md p-2"
                  />
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-2 mb-3">
              {!isWoot && (
                <button
                  onPointerDown={handleTrackClick}
                  disabled={isTrackingProduct}
                  className={`flex items-center justify-center px-3 py-2 rounded-md transition-colors text-sm font-medium border ${
                    isTracked
                      ? "bg-emerald-100 text-emerald-700 border-emerald-200 hover:bg-emerald-200"
                      : "bg-emerald-600 text-white border-emerald-600 hover:bg-emerald-700"
                  } ${isTrackingProduct ? "opacity-50" : ""}`}
                >
                  {isTrackingProduct ? (
                    <div className="w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin mr-1.5" />
                  ) : isTracked ? (
                    <MonitorOff className="h-3 w-3 mr-1.5" />
                  ) : (
                    <Bell className="h-3 w-3 mr-1.5" />
                  )}
                  {isTracked ? t("products.trackingActiveTitle") : t("tracking.priceAlert")}
                </button>
              )}

              {!isFreeUser && <button
                  onPointerDown={handleFavoriteToggle}
                  disabled={isUpdatingFavorite}
                  className={`flex items-center justify-center px-3 py-2 rounded-md transition-colors text-sm font-medium border ${
                    favoriteStatus.isFavorite
                      ? "bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 border-red-200 dark:border-red-700/50 hover:bg-red-200 dark:hover:bg-red-900/50"
                      : "bg-white dark:bg-dark-800 text-gray-700 dark:text-dark-200 border-gray-300 dark:border-dark-600 hover:bg-gray-50 dark:hover:bg-dark-700"
                  } ${isUpdatingFavorite ? "opacity-50" : ""}`}
                >
                  {isUpdatingFavorite ? (
                    <div className="w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin mr-1.5" />
                  ) : (
                    <Heart
                      className={`h-3 w-3 mr-1.5 ${
                        favoriteStatus.isFavorite ? "fill-current" : ""
                      }`}
                    />
                  )}
                  {favoriteStatus.isFavorite ? t("products.favorited") : t("products.favorite")}
                </button>}

            </div>

            {/* Last Updated */}
            {lastUpdated && (
              <div className="pt-2 border-t border-gray-200 dark:border-dark-700">
                <div className="flex items-center text-xs text-gray-600 dark:text-dark-300">
                  <Calendar className="h-3 w-3 mr-1.5" />
                  {t("tracking.lastChecked")} {lastUpdated.toLocaleString()}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="mb-3">
          <OtherStoreAvailability
            productIdentifier={productIdentifier}
            currentStoreId={product.storeId}
            retailer={retailer || "walmart"}
          />
        </div>

        {/* Charts Section */}
        {chartData.length > 1 ? (
          <div className="space-y-3">
            {/* Price History Chart */}
            <div className="bg-white dark:bg-dark-900 border border-gray-200 dark:border-dark-700 rounded-lg p-3">
              <div className="flex items-center justify-between mb-2">
                <h2 className="text-sm font-bold text-gray-900 dark:text-white">
                  Price History
                </h2>
                <TrendingUp className="h-4 w-4 text-primary-600 dark:text-primary-400" />
              </div>
              <ResponsiveContainer width="100%" height={180}>
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke={isDark ? "#374151" : "#f3f4f6"} />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 10, fill: isDark ? "#9ca3af" : "#6b7280" }}
                    tickLine={{ stroke: isDark ? "#4b5563" : "#d1d5db" }}
                    axisLine={{ stroke: isDark ? "#4b5563" : "#d1d5db" }}
                  />
                  <YAxis
                    tick={{ fontSize: 10, fill: isDark ? "#9ca3af" : "#6b7280" }}
                    tickFormatter={(value) => `$${value.toFixed(2)}`}
                    tickLine={{ stroke: isDark ? "#4b5563" : "#d1d5db" }}
                    axisLine={{ stroke: isDark ? "#4b5563" : "#d1d5db" }}
                  />
                  <Tooltip
                    formatter={(value: number) => [
                      `$${value.toFixed(2)}`,
                      "Price",
                    ]}
                    labelFormatter={(label) => `Date: ${label}`}
                    contentStyle={{
                      backgroundColor: isDark ? "#1f2937" : "white",
                      border: isDark ? "1px solid #374151" : "1px solid #d1d5db",
                      borderRadius: "6px",
                      fontSize: "12px",
                      color: isDark ? "#e5e7eb" : undefined,
                    }}
                  />
                  <Line
                    type="monotone"
                    dataKey="price"
                    stroke="#6A0DAD"
                    strokeWidth={2}
                    dot={{ fill: "#6A0DAD", strokeWidth: 1, r: 3 }}
                    activeDot={{
                      r: 4,
                      stroke: "#6A0DAD",
                      strokeWidth: 2,
                      fill: isDark ? "#1f2937" : "white",
                    }}
                    name="Store Price"
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>

            {/* Inventory Chart */}
            <div className="bg-white dark:bg-dark-900 border border-gray-200 dark:border-dark-700 rounded-lg p-3">
              <div className="flex items-center justify-between mb-2">
                <h2 className="text-sm font-bold text-gray-900 dark:text-white">
                  Inventory Trend
                </h2>
                <Package className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
              </div>
              <ResponsiveContainer width="100%" height={180}>
                <AreaChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke={isDark ? "#374151" : "#f3f4f6"} />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 10, fill: isDark ? "#9ca3af" : "#6b7280" }}
                    tickLine={{ stroke: isDark ? "#4b5563" : "#d1d5db" }}
                    axisLine={{ stroke: isDark ? "#4b5563" : "#d1d5db" }}
                  />
                  <YAxis
                    tick={{ fontSize: 10, fill: isDark ? "#9ca3af" : "#6b7280" }}
                    tickLine={{ stroke: isDark ? "#4b5563" : "#d1d5db" }}
                    axisLine={{ stroke: isDark ? "#4b5563" : "#d1d5db" }}
                  />
                  <Tooltip
                    formatter={(value: number) => [
                      `${value} units`,
                      "Inventory",
                    ]}
                    labelFormatter={(label) => `Date: ${label}`}
                    contentStyle={{
                      backgroundColor: isDark ? "#1f2937" : "white",
                      border: isDark ? "1px solid #374151" : "1px solid #d1d5db",
                      borderRadius: "6px",
                      fontSize: "12px",
                      color: isDark ? "#e5e7eb" : undefined,
                    }}
                  />
                  <Area
                    type="monotone"
                    dataKey="inventory"
                    stroke="#16a34a"
                    fill="url(#inventoryGradient)"
                    strokeWidth={2}
                    name="Available Units"
                  />
                  <defs>
                    <linearGradient
                      id="inventoryGradient"
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1"
                    >
                      <stop offset="5%" stopColor="#16a34a" stopOpacity={0.2} />
                      <stop
                        offset="95%"
                        stopColor="#16a34a"
                        stopOpacity={0.05}
                      />
                    </linearGradient>
                  </defs>
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
        ) : (
          <div className="bg-white dark:bg-dark-900 border border-gray-200 dark:border-dark-700 rounded-lg p-4 text-center">
            <div className="max-w-xs mx-auto">
              <TrendingUp className="h-8 w-8 text-gray-400 dark:text-dark-500 mx-auto mb-2" />
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
                Charts Coming Soon
              </h3>
              <p className="text-xs text-gray-600 dark:text-dark-300">
                Historical data charts will appear once this product has been
                tracked over multiple time periods.
              </p>
            </div>
          </div>
        )}

        {/* Store Location Card */}
        {product && !isOnlineOnly && (
          <div className="mt-6">
            <StoreLocationCard 
              storeId={product.storeId} 
              retailer={retailer as "walmart" | "home_depot" | "dollar_general" | undefined} 
            />
          </div>
        )}

        {/* Tracking Modal */}
        {showTrackingModal && (
          <div
            className="fixed inset-0 flex items-center justify-center z-50 p-4"
            style={{
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              backdropFilter: "blur(4px)",
            }}
            onPointerDown={() => setShowTrackingModal(false)}
          >
            <div
              className="bg-white dark:bg-dark-800 rounded-xl p-6 max-w-sm w-full"
              onPointerDown={(e) => e.stopPropagation()}
            >
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">{t("tracking.priceAlert")}</h3>
              <p className="text-sm text-gray-600 dark:text-dark-300 mb-4">
                We'll notify you when the price drops to or below your target.
              </p>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-dark-200 mb-1">
                    {t("tracking.currentPrice")}: {formatCurrency(currentPrice)}
                  </label>
                  <label className="block text-sm font-medium text-gray-700 dark:text-dark-200 mb-2">
                    {t("tracking.targetPrice")}
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0.01"
                    value={targetPrice}
                    onChange={(e) => setTargetPrice(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-dark-600 bg-white dark:bg-dark-900 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="Enter target price"
                  />
                </div>

                <div className="flex space-x-3">
                  <button
                    onPointerDown={handleConfirmTracking}
                    disabled={isTrackingProduct}
                    className="flex-1 bg-primary-600 text-white py-2 px-4 rounded-lg font-medium hover:bg-primary-700 disabled:opacity-50"
                  >
                    {isTrackingProduct ? t("settings.processing") : t("products.track")}
                  </button>
                  <button
                    onPointerDown={() => setShowTrackingModal(false)}
                    disabled={isTrackingProduct}
                    className="flex-1 bg-gray-100 dark:bg-dark-700 text-gray-700 dark:text-dark-200 py-2 px-4 rounded-lg font-medium hover:bg-gray-200 dark:hover:bg-dark-600"
                  >
                    {t("common.cancel")}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}
